import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  Stamp,
  Home,
  Plane,
  Wallet,
  Headphones,
  ArrowRight,
  Check,
  LayoutGrid,
} from "lucide-react";
import LandingLayout from "layouts/LandingLayout";
import Hero, { SlantedBackground } from "components/hero/BackgroundAsImage";
import { SERVICES, ServiceItem } from "constants/services";
import CtaFinal from "components/cta/CtaFinal";
import useInView from "helpers/useInView";
import {
  fadeUpVariants,
  staggerContainerVariants,
  staggerItemVariants,
} from "lib/animations";
import { IMAGES } from "constants/images";

// ─── Icônes par slug ──────────────────────────────────────────────────────────

const SERVICE_ICONS: Record<string, React.ReactNode> = {
  avi: <FileText size={32} />,
  visa: <Stamp size={32} />,
  logement: <Home size={32} />,
  billets: <Plane size={32} />,
  finances: <Wallet size={32} />,
  support: <Headphones size={32} />,
};

// ─── Animation ───────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

/** Wrapper qui anime ses enfants à l'entrée dans le viewport */
const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </MDiv>
  );
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const PageWrapper = tw.div`max-w-screen-xl mx-auto px-6 py-20`;

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TabsRow = tw.div`flex flex-wrap gap-2 justify-center mb-16 border-b border-gray-200 pb-6`;

const Tab = styled.button<{ $active: boolean }>`
  ${tw`flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all duration-200 border-b-2`}
  border-color: ${(p) => (p.$active ? "#ff6c00" : "transparent")};
  color: ${(p) => (p.$active ? "#ff6c00" : "#6b7280")};
  background: transparent;
  transform: ${(p) => (p.$active ? "translateY(2px)" : "none")};

  &:hover {
    color: #ff6c00;
  }
`;

// ── Service card ──────────────────────────────────────────────────────────────
const ServiceCard = styled.div<{ $even: boolean }>`
  ${tw`flex flex-col gap-0 mb-24`}
  ${(p) => (p.$even ? tw`lg:flex-row-reverse` : tw`lg:flex-row`)}
`;

const ImageCol = tw.div`lg:w-5/12 relative overflow-hidden`;

const ServiceImage = styled.div<{ $src: string; $color: string }>`
  ${tw`bg-cover bg-center w-full h-full relative`}
  min-height: 22rem;
  background-image: url(${(p) => p.$src});

  &::after {
    content: "";
    ${tw`absolute inset-0`}
    background: linear-gradient(
      to bottom,
      transparent 40%,
      ${(p) => p.$color}99 100%
    );
  }
`;

const PriceBadge = styled.div<{ $color: string }>`
  ${tw`absolute top-0 left-0 px-5 py-3 font-black text-white text-base z-10`}
  background: ${(p) => p.$color};
`;

const InfoCol = tw.div`lg:w-7/12 flex flex-col justify-center px-0 lg:px-16 pt-8 lg:pt-0`;

const ServiceIconWrap = styled.div<{ $color: string }>`
  ${tw`w-16 h-16 flex items-center justify-center mb-6`}
  background: ${(p) => p.$color}15;
  color: ${(p) => p.$color};
  border-left: 3px solid ${(p) => p.$color};
`;

const ServiceTag = styled.p<{ $color: string }>`
  ${tw`text-xs font-black uppercase tracking-widest mb-3`}
  color: ${(p) => p.$color};
`;

const ServiceTitle = tw.h2`text-3xl font-black text-gray-900 mb-4 leading-tight`;

const ServiceDesc = tw.p`text-gray-500 leading-relaxed mb-8 text-base`;

const FeaturesGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 gap-0 mb-8 border border-gray-200`;

const FeatureItem = styled.div<{ $color: string }>`
  ${tw`flex items-center gap-3 px-4 py-3 border-b border-r border-gray-200`}

  .check-icon {
    color: ${(p) => p.$color};
    flex-shrink: 0;
  }
`;

const FeatureText = tw.span`text-sm text-gray-600`;

const ServiceCTA = styled(Link)<{ $color: string }>`
  ${tw`inline-flex items-center gap-3 px-8 py-4 font-black text-white no-underline transition-all duration-300 self-start`}
  background: ${(p) => p.$color};

  &:hover {
    filter: brightness(0.9);
    gap: 2rem;
  }
`;

const Divider = styled.hr`
  ${tw`border-0 mb-24`}
  border-top: 1px solid #f0f0f0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const ServicesPage: React.FC = () => {
  const [activeSlug, setActiveSlug] = useState<string>("all");

  const filtered: ServiceItem[] =
    activeSlug === "all"
      ? SERVICES
      : SERVICES.filter((s) => s.slug === activeSlug);

  return (
    <LandingLayout disableAnimation>
      <Hero
        backgroundImageSrc={IMAGES.serviceImg}
        notification="Nos Services"
        heading={
          <>
            <span>Solutions complètes pour</span>
            <br />
            <SlantedBackground>votre mobilité.</SlantedBackground>
          </>
        }
        description="Chaque service a été conçu pour simplifier votre parcours et maximiser vos chances de succès. Tarifs transparents, délais garantis."
      />

      <PageWrapper>
        {/* ── Tabs ── */}
        <FadeUpSection>
          <TabsRow>
            <Tab
              $active={activeSlug === "all"}
              onClick={() => setActiveSlug("all")}
            >
              <LayoutGrid size={15} />
              Tous les services
            </Tab>
            {SERVICES.map((s) => (
              <Tab
                key={s.slug}
                $active={activeSlug === s.slug}
                onClick={() => setActiveSlug(s.slug)}
              >
                {SERVICE_ICONS[s.slug]}
                {s.title}
              </Tab>
            ))}
          </TabsRow>
        </FadeUpSection>

        {/* ── Service detail cards ── */}
        <MDiv
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {filtered.map((service, index) => (
            <MDiv key={service.slug} variants={staggerItemVariants}>
              <ServiceCard $even={index % 2 === 1}>
                {/* Image */}
                <ImageCol>
                  <ServiceImage $src={service.imageSrc} $color={service.color}>
                    {service.price && (
                      <PriceBadge $color={service.color}>
                        {service.price}
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: 400,
                            marginLeft: "0.35rem",
                            opacity: 0.85,
                          }}
                        >
                          {service.priceLabel}
                        </span>
                      </PriceBadge>
                    )}
                  </ServiceImage>
                </ImageCol>

                {/* Info */}
                <InfoCol>
                  <ServiceIconWrap $color={service.color}>
                    {SERVICE_ICONS[service.slug]}
                  </ServiceIconWrap>

                  <ServiceTag $color={service.color}>
                    Service MomsEduc
                  </ServiceTag>

                  <ServiceTitle>{service.title}</ServiceTitle>

                  <ServiceDesc>{service.description}</ServiceDesc>

                  <FeaturesGrid>
                    {service.features.map((f) => (
                      <FeatureItem key={f} $color={service.color}>
                        <Check size={14} className="check-icon" />
                        <FeatureText>{f}</FeatureText>
                      </FeatureItem>
                    ))}
                  </FeaturesGrid>

                  <ServiceCTA to="/contact" $color={service.color}>
                    Commencer ma démarche
                    <ArrowRight size={16} />
                  </ServiceCTA>
                </InfoCol>
              </ServiceCard>

              {index < filtered.length - 1 && <Divider />}
            </MDiv>
          ))}
        </MDiv>
      </PageWrapper>

      {/* ── CTA Final ── */}
      <CtaFinal />
    </LandingLayout>
  );
};

export default ServicesPage;
